import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";
import { influencerProducts, topStars } from "@/mocks/influencerData";

const platforms = [
  { id: "facebook", name: "Facebook", icon: "ri-facebook-fill", color: "text-white", bg: "bg-white/15 border-white/30" },
  { id: "youtube", name: "YouTube", icon: "ri-youtube-fill", color: "text-white", bg: "bg-white/15 border-white/30" },
  { id: "tiktok", name: "TikTok", icon: "ri-tiktok-fill", color: "text-white", bg: "bg-white/15 border-white/30" },
  { id: "twitter", name: "Twitter / X", icon: "ri-twitter-x-fill", color: "text-white", bg: "bg-white/15 border-white/30" },
];

// Mock dashboard data for demo
const mockSalesData = [
  { product: "Premium Oud & Frankincense Perfume Set", sold: 48, commission: 854.4, flag: "🇴🇲" },
  { product: "Dead Sea Mineral Skincare Gift Set", sold: 32, commission: 960, flag: "🇮🇱" },
  { product: "Turkish Handcrafted Leather Bag", sold: 21, commission: 669.9, flag: "🇹🇷" },
  { product: "Sialkot FIFA Pro Football", sold: 65, commission: 760.5, flag: "🇵🇰" },
  { product: "Authentic Gaziantep Baklava Gift Box", sold: 89, commission: 1032.4, flag: "🇹🇷" },
];

type TabType = "products" | "signup" | "dashboard";

const InfluencerPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>("products");
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [filterCategory, setFilterCategory] = useState("all");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", platform: "", handle: "",
    followers: "", country: "", niche: "", paypalEmail: "", message: "", agreeTerms: false,
  });

  const categories = ["all", ...Array.from(new Set(influencerProducts.map((p) => p.category)))];
  const filtered = filterCategory === "all" ? influencerProducts : influencerProducts.filter((p) => p.category === filterCategory);

  const toggleProduct = (id: string) => {
    setSelectedProducts((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    setForm((prev) => ({ ...prev, [target.name]: target.type === "checkbox" ? target.checked : target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.agreeTerms) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, String(v)));
      body.append("selected_products", selectedProducts.join(", "));
      await fetch("https://readdy.ai/api/form/d7ekaldfi84lst2k05ug", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch { setSubmitted(true); } finally { setSubmitting(false); }
  };

  const totalMockCommission = mockSalesData.reduce((s, d) => s + d.commission, 0);
  const totalMockSold = mockSalesData.reduce((s, d) => s + d.sold, 0);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Influencer Program – Earn 30% Commission from Middle East Factories | SuperMiddleEast"
        description="Join the SuperMiddleEast Global Star Program. Promote verified factory products and earn up to 30% commission paid via PayPal. Open to influencers from 1K followers worldwide."
        keywords="influencer program Middle East, factory affiliate program, earn commission, dropshipping influencer, PayPal commission, social media affiliate"
        canonical={`${BASE_URL}/influencer-program`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Influencer Program – SuperMiddleEast Global Star Program",
            "description": "Join the SuperMiddleEast Global Star Program. Promote verified factory products and earn up to 30% commission paid via PayPal.",
            "url": `${BASE_URL}/influencer-program`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "Influencer Program", "item": `${BASE_URL}/influencer-program` },
              ],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "SuperMiddleEast Global Star Influencer Program",
            "description": "Earn up to 30% commission by promoting verified Middle East factory products on social media platforms including Facebook, YouTube, TikTok and Twitter.",
            "url": `${BASE_URL}/influencer-program`,
            "provider": {
              "@type": "Organization",
              "name": "SuperMiddleEast Factory and Buyer",
              "url": BASE_URL,
            },
            "offers": {
              "@type": "Offer",
              "description": "Up to 30% commission per sale, paid monthly via PayPal",
            },
          },
        ]}
      />
      <Navbar />

      {/* Hero — Gold */}
      <section className="relative pt-20 pb-0 overflow-hidden" style={{ background: "linear-gradient(135deg, #92400e 0%, #b45309 20%, #d97706 40%, #f59e0b 55%, #fbbf24 65%, #f59e0b 75%, #d97706 88%, #92400e 100%)" }}>
        <div className="absolute inset-0 opacity-15" style={{ background: "repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.05) 40px, rgba(255,255,255,0.05) 80px)" }}></div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-200 via-white to-yellow-200 opacity-60"></div>

        <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-8 pt-14 pb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 border border-white/40 text-yellow-100 text-xs font-bold px-5 py-2 rounded-full mb-6">
            <i className="ri-star-fill text-yellow-200"></i>
            Global Star Program · 5 Million Verified Factories · PayPal Commissions
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}>
            Global Stars Help
            <br />
            <span className="text-yellow-200">Middle East Factories</span>
            <br />
            Sell to the World
          </h1>
          <p className="text-yellow-100 text-lg max-w-2xl mx-auto mb-8">
            Choose products from verified factories · Track sales in real-time · Earn up to <strong className="text-white">30% commission</strong> paid via <strong className="text-white">PayPal</strong>
          </p>
          <div className="flex items-center justify-center gap-5 mb-10">
            {platforms.map((p) => (
              <div key={p.id} className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 flex items-center justify-center bg-white/20 border border-white/30 rounded-2xl">
                  <i className={`${p.icon} text-2xl text-white`}></i>
                </div>
                <span className="text-yellow-100 text-xs font-medium">{p.name}</span>
              </div>
            ))}
          </div>
          {/* Tab Nav */}
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { key: "products", label: "Pick Products", icon: "ri-box-3-line" },
              { key: "signup", label: "Join as Star", icon: "ri-user-add-line" },
              { key: "dashboard", label: "Sales Dashboard", icon: "ri-bar-chart-line" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as TabType)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key ? "bg-white text-amber-700" : "bg-white/20 border border-white/40 text-white hover:bg-white/30"}`}
              >
                <i className={tab.icon}></i>{tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-16 bg-amber-50 min-h-screen">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">

          {/* ── PRODUCT PICKER ── */}
          {activeTab === "products" && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Choose Products from Verified Factories
                </h2>
                <p className="text-gray-500 text-sm">Select products that match your audience. All from verified Middle East &amp; global factories with local warehouse shipping.</p>
                {selectedProducts.length > 0 && (
                  <div className="inline-flex items-center gap-2 bg-amber-500 text-white text-sm font-bold px-4 py-2 rounded-full mt-3">
                    <i className="ri-checkbox-circle-fill"></i>
                    {selectedProducts.length} product{selectedProducts.length > 1 ? "s" : ""} selected
                    <button onClick={() => setActiveTab("signup")} className="underline cursor-pointer ms-2">Continue to Sign Up</button>
                  </div>
                )}
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-6 justify-center">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setFilterCategory(cat)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap capitalize ${filterCategory === cat ? "bg-amber-500 text-white" : "bg-white border border-amber-200 text-gray-600 hover:border-amber-400"}`}
                  >
                    {cat === "all" ? "All Categories" : cat}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop>
                {filtered.map((product) => {
                  const selected = selectedProducts.includes(product.id);
                  return (
                    <div
                      key={product.id}
                      onClick={() => toggleProduct(product.id)}
                      className={`bg-white rounded-2xl overflow-hidden cursor-pointer border-2 transition-all duration-200 hover:-translate-y-1 ${selected ? "border-amber-500 ring-2 ring-amber-200" : "border-transparent hover:border-amber-200"}`}
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                        {selected && (
                          <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                            <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-full">
                              <i className="ri-check-line text-white text-2xl"></i>
                            </div>
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          <span className="bg-amber-500 text-white text-xs font-black px-2 py-1 rounded-lg">{product.commissionRate}% Comm.</span>
                        </div>
                        <div className="absolute top-2 right-2">
                          <span className="bg-white/90 text-gray-700 text-xs font-bold px-2 py-1 rounded-lg">{product.factoryFlag} {product.factoryCountry}</span>
                        </div>
                        <div className="absolute bottom-2 left-2 flex gap-1">
                          {product.verified && <span className="bg-emerald-500 text-white text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5"><i className="ri-shield-check-line text-xs"></i> Verified</span>}
                          {product.dropshipping && <span className="bg-amber-600 text-white text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5"><i className="ri-truck-line text-xs"></i> Dropship</span>}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="text-xs text-amber-600 font-semibold mb-1 truncate">{product.factory}</div>
                        <div className="font-bold text-gray-900 text-sm mb-3 line-clamp-2 leading-tight">{product.name}</div>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="text-xs text-gray-400">Retail Price</div>
                            <div className="font-bold text-gray-700">${product.retailPrice}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-400">You earn</div>
                            <div className="font-black text-emerald-600 text-lg">${product.commissionAmount.toFixed(0)}</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                          <span><i className="ri-shopping-bag-line mr-1"></i>{product.totalSold.toLocaleString()} sold</span>
                          <span><i className="ri-map-pin-line mr-1"></i>{product.warehouseCountries.length} warehouses</span>
                        </div>
                        <div className={`w-full py-2 rounded-xl text-xs font-bold text-center transition-all ${selected ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                          {selected ? "✓ Selected to Promote" : "Select to Promote"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedProducts.length > 0 && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setActiveTab("signup")}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-black px-10 py-4 rounded-2xl text-base transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-line"></i>
                    Continue to Sign Up ({selectedProducts.length} products selected)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── SIGN UP ── */}
          {activeTab === "signup" && (
            <div className="max-w-3xl mx-auto">
              {submitted ? (
                <div className="bg-white rounded-3xl border border-amber-100 p-10 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-5">
                    <i className="ri-star-fill text-amber-500 text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Welcome to the Global Star Program!</h3>
                  <p className="text-gray-500 text-base mb-5">Your application has been received. Our team will review and contact you within 48 hours. Get ready to start earning!</p>
                  <div className="bg-amber-50 rounded-2xl p-5 text-left space-y-3 text-sm text-gray-600 mb-6">
                    <div className="flex items-center gap-2"><i className="ri-mail-check-line text-amber-500"></i> Confirmation sent to {form.email}</div>
                    <div className="flex items-center gap-2"><i className="ri-time-line text-amber-500"></i> Review within 48 hours</div>
                    <div className="flex items-center gap-2"><i className="ri-gift-line text-amber-500"></i> Free product sample kit on approval</div>
                    <div className="flex items-center gap-2"><i className="ri-paypal-fill text-amber-500"></i> PayPal commissions set up on approval</div>
                  </div>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-amber-100 p-8">
                  <h2 className="text-2xl font-black text-gray-900 mb-2 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <i className="ri-star-fill text-amber-500"></i> Join the Global Star Program
                  </h2>
                  <p className="text-gray-500 text-sm mb-6">Fill in your details. Earn up to 30% commission paid via PayPal on every sale you drive.</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} required placeholder="Your full name" className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Email Address *</label>
                      <input name="email" value={form.email} onChange={handleChange} type="email" required placeholder="your@email.com" className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">PayPal Email (for commissions) *</label>
                      <div className="relative">
                        <i className="ri-paypal-fill absolute left-3 top-1/2 -translate-y-1/2 text-lg" style={{ color: "#003087" }}></i>
                        <input name="paypalEmail" value={form.paypalEmail} onChange={handleChange} type="email" required placeholder="paypal@email.com" className="w-full border-2 border-amber-100 rounded-xl pl-9 pr-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Phone Number</label>
                      <input name="phone" value={form.phone} onChange={handleChange} placeholder="+1 000 000 0000" className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Country *</label>
                      <select name="country" value={form.country} onChange={handleChange} required className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                        <option value="">Select country...</option>
                        {["USA","UK","UAE","Saudi Arabia","Germany","France","Australia","Canada","Malaysia","Indonesia","Turkey","Pakistan","Egypt","Jordan","Kuwait","Qatar","Oman","Bahrain","Other"].map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Primary Platform *</label>
                      <div className="grid grid-cols-2 gap-2">
                        {platforms.map((p) => (
                          <label key={p.id} className={`flex items-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${form.platform === p.id ? "border-amber-400 bg-amber-50" : "border-gray-100 hover:border-amber-200"}`}>
                            <input type="radio" name="platform" value={p.id} checked={form.platform === p.id} onChange={handleChange} className="sr-only" />
                            <i className={`${p.icon} text-lg ${form.platform === p.id ? "text-amber-600" : "text-gray-500"}`}></i>
                            <span className="text-xs font-bold text-gray-800">{p.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Social Media Handle *</label>
                      <input name="handle" value={form.handle} onChange={handleChange} required placeholder="@yourhandle" className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Follower Count *</label>
                      <select name="followers" value={form.followers} onChange={handleChange} required className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                        <option value="">Select range...</option>
                        {["1K – 10K","10K – 50K","50K – 100K","100K – 500K","500K – 1M","1M – 5M","5M+"].map((f) => <option key={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Content Niche</label>
                      <select name="niche" value={form.niche} onChange={handleChange} className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                        <option value="">Select niche...</option>
                        {["Fashion & Lifestyle","Tech & Gadgets","Home & Decor","Food & Cooking","Beauty & Skincare","Business & Finance","Travel","Sports & Fitness","Luxury","General"].map((n) => <option key={n}>{n}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Why join? Tell us about your audience <span className="text-gray-400 font-normal">(max 500 chars)</span></label>
                      <textarea name="message" value={form.message} onChange={handleChange} rows={3} maxLength={500} placeholder="Tell us about your audience and why you'd be a great partner..." className="w-full border-2 border-amber-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 resize-none" />
                      <div className="text-right text-xs text-gray-400 mt-1">{form.message.length}/500</div>
                    </div>
                  </div>

                  {selectedProducts.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-center justify-between">
                      <div>
                        <div className="text-xs font-bold text-amber-700 mb-0.5"><i className="ri-box-3-line mr-1"></i>{selectedProducts.length} product(s) selected to promote</div>
                        <div className="text-xs text-gray-400">These will be linked to your affiliate account</div>
                      </div>
                      <button type="button" onClick={() => setActiveTab("products")} className="text-amber-600 text-xs font-bold underline cursor-pointer whitespace-nowrap">Change</button>
                    </div>
                  )}

                  {/* PayPal info box */}
                  <div className="mt-5 p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
                    <i className="ri-paypal-fill text-2xl mt-0.5" style={{ color: "#003087" }}></i>
                    <div>
                      <div className="font-bold text-gray-800 text-sm mb-0.5">Commission paid via PayPal</div>
                      <p className="text-xs text-gray-500 leading-relaxed">All commissions (18%–30%) are paid monthly to your PayPal account. Make sure your PayPal email above is correct. Available in 190+ countries.</p>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer mt-5 mb-5">
                    <input type="checkbox" name="agreeTerms" checked={form.agreeTerms} onChange={handleChange} className="w-4 h-4 accent-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">I agree to the <a href="#" className="text-amber-600 underline" rel="nofollow">Influencer Program Terms</a> and <a href="#" className="text-amber-600 underline" rel="nofollow">Commission Policy</a>.</span>
                  </label>

                  <button type="submit" disabled={!form.agreeTerms || submitting} className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-black py-4 rounded-2xl text-base transition-colors cursor-pointer flex items-center justify-center gap-2">
                    <i className="ri-star-fill"></i>
                    {submitting ? "Submitting..." : "Submit Application — Join the Global Star Program"}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── SALES DASHBOARD ── */}
          {activeTab === "dashboard" && (
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Influencer Sales Dashboard
                </h2>
                <p className="text-gray-500 text-sm">Track your product sales, commissions earned, and PayPal payouts in real-time.</p>
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-bold px-4 py-2 rounded-full mt-3">
                  <i className="ri-information-line"></i>
                  Demo dashboard — sign up to access your live dashboard
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { icon: "ri-shopping-bag-line", label: "Total Units Sold", value: totalMockSold.toString(), color: "text-amber-500", bg: "bg-amber-50" },
                  { icon: "ri-money-dollar-circle-line", label: "Total Commission", value: `$${totalMockCommission.toFixed(0)}`, color: "text-emerald-600", bg: "bg-emerald-50" },
                  { icon: "ri-paypal-fill", label: "Paid via PayPal", value: "$3,120", color: "text-amber-500", bg: "bg-amber-50" },
                  { icon: "ri-time-line", label: "Pending Payout", value: `$${(totalMockCommission - 3120).toFixed(0)}`, color: "text-rose-500", bg: "bg-rose-50" },
                ].map((s) => (
                  <div key={s.label} className="bg-white border border-amber-100 rounded-2xl p-5">
                    <div className={`w-10 h-10 flex items-center justify-center ${s.bg} rounded-xl mb-3`}>
                      <i className={`${s.icon} ${s.color} text-lg`}></i>
                    </div>
                    <div className="font-black text-gray-900 text-xl leading-none mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</div>
                    <div className="text-gray-400 text-xs">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Sales Table */}
              <div className="bg-white border border-amber-100 rounded-2xl overflow-hidden mb-8">
                <div className="px-6 py-4 border-b border-amber-50 flex items-center justify-between">
                  <h3 className="font-black text-gray-900 text-base">Product Sales Breakdown</h3>
                  <span className="text-xs text-gray-400">This Month</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-amber-50">
                        <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Units Sold</th>
                        <th className="text-right px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockSalesData.map((row, i) => (
                        <tr key={i} className="border-t border-amber-50 hover:bg-amber-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{row.flag}</span>
                              <span className="text-sm font-medium text-gray-800 line-clamp-1">{row.product}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="bg-amber-100 text-amber-700 font-bold text-sm px-3 py-1 rounded-full">{row.sold}</span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="font-black text-emerald-600 text-base">${row.commission.toFixed(0)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-amber-200 bg-amber-50">
                        <td className="px-6 py-4 font-black text-gray-900 text-sm">Total</td>
                        <td className="px-4 py-4 text-center font-black text-gray-900">{totalMockSold}</td>
                        <td className="px-6 py-4 text-right font-black text-emerald-600 text-lg">${totalMockCommission.toFixed(0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* PayPal Payout Info */}
              <div className="bg-white border border-amber-100 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-5">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-2xl flex-shrink-0">
                  <i className="ri-paypal-fill text-3xl" style={{ color: "#003087" }}></i>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="font-black text-gray-900 text-lg mb-1">PayPal Commission Payout</div>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Commissions are calculated monthly and paid directly to your PayPal account on the <strong>1st of each month</strong>. Minimum payout: <strong>$50</strong>. Available in <strong>190+ countries</strong>. No hidden fees.
                  </p>
                </div>
                <div className="flex flex-col gap-2 text-center flex-shrink-0">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3">
                    <div className="text-xs text-gray-400 mb-0.5">Next Payout</div>
                    <div className="font-black text-emerald-600 text-xl">${(totalMockCommission - 3120).toFixed(0)}</div>
                    <div className="text-xs text-gray-400">May 1, 2026</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setActiveTab("signup")}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-black px-10 py-4 rounded-2xl text-base transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-user-add-line"></i>
                  Sign Up to Access Your Live Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Top Stars */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Top Earning Global Stars</h2>
            <p className="text-gray-500 text-sm mt-2">Real influencers earning real commissions from verified factory products</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {topStars.map((star, idx) => (
              <div key={star.id} className="bg-amber-50 border border-amber-100 rounded-2xl p-6 text-center hover:-translate-y-1 transition-transform duration-300">
                <div className="relative w-16 h-16 mx-auto mb-3">
                  <img src={star.avatar} alt={star.name} className="w-16 h-16 rounded-full object-cover border-2 border-amber-300" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full text-xs font-black text-white">#{idx + 1}</div>
                </div>
                <div className="font-black text-gray-900 text-base mb-0.5">{star.name}</div>
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <span className="text-gray-400 text-xs">{star.flag} {star.country}</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 mb-3">
                  <i className={`${star.platformIcon} text-amber-600 text-sm`}></i>
                  <span className="text-amber-600 text-xs font-semibold">{star.followers} followers</span>
                </div>
                <div className="bg-white rounded-xl p-3 border border-amber-100">
                  <div className="text-xs text-gray-400 mb-0.5">Commission Earned</div>
                  <div className="font-black text-amber-600 text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>${star.totalCommission.toLocaleString()}</div>
                  <div className="text-xs text-gray-400 mt-0.5">${star.totalSales.toLocaleString()} in total sales</div>
                </div>
                <div className="mt-2 text-xs text-gray-400 italic">{star.niche}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InfluencerPage;
